import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Rocket,
  Cpu,
  Terminal,
  Globe,
  Cog,
  GraduationCap,
  Compass,
  Utensils,
  Wind,
  Wrench,
  Trees,
  Hammer,
  Briefcase,
  Store,
  Building2,
  ShieldCheck,
  Award,
  Handshake,
  Zap,
  Clock,
  DollarSign,
  BookOpen,
  ChevronDown,
  CheckCircle2,
  CircleDot,
  Send,
} from "lucide-react";
import { Toaster } from "sonner";
import { SERVICES } from "@/constants/testIds";
import ConsultationModal from "@/components/services/ConsultationModal";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_prompt-mastery-64/artifacts/3omzwe9h_1773800644378.png";
const CYPHER_IMG = "/cypher.webp";

/* ==========================================================
   1. LENIS SMOOTH SCROLL (services page only)
   ========================================================== */
function useLenis() {
  const prefersReduced = useReducedMotion();
  useEffect(() => {
    if (prefersReduced) return undefined;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [prefersReduced]);
}

/* ==========================================================
   Shared bits
   ========================================================== */
const Container = ({ className = "", children }) => (
  <div className={`max-w-7xl mx-auto px-6 sm:px-8 ${className}`}>{children}</div>
);

const SectionLabel = ({ n, children }) => (
  <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-cyan-400">
    <span className="text-slate-500">CHAPTER {n}</span>
    <span className="h-px w-8 bg-cyan-400/40" />
    <span>{children}</span>
  </div>
);

const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ==========================================================
   Header (route-aware, matches landing style)
   ========================================================== */
const Header = ({ onBook }) => (
  <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/10">
    <Container className="flex items-center justify-between h-16 sm:h-20">
      <Link to="/" data-testid={SERVICES.navServicesLogo} className="flex items-center gap-3">
        <img src={LOGO_URL} alt="JK Academy" className="h-9 w-9 sm:h-10 sm:w-10 object-contain" />
        <div className="leading-tight">
          <div className="font-display font-extrabold tracking-tight text-white text-base sm:text-lg">
            JK ACADEMY
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400 hidden sm:block">
            Services · AI for Small Business
          </div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-7 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
        <Link to="/" data-testid={SERVICES.navHome} className="hover:text-cyan-300 transition-colors">
          Courses
        </Link>
        <a href="#services-grid" className="hover:text-cyan-300 transition-colors">Services</a>
        <a href="#process" className="hover:text-cyan-300 transition-colors">Process</a>
        <a href="#faq" className="hover:text-cyan-300 transition-colors">FAQ</a>
      </nav>
      <button
        data-testid={SERVICES.navConsult}
        onClick={onBook}
        className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-[10px] sm:text-[11px] px-3 sm:px-4 py-2 sm:py-2.5 shadow-[0_0_20px_rgba(6,182,212,0.35)] hover:shadow-[0_0_36px_rgba(6,182,212,0.6)] transition-all"
      >
        <span className="hidden sm:inline">Book Free Call</span>
        <span className="sm:hidden">Book Call</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </Container>
  </header>
);

/* ==========================================================
   2. HERO — kinetic, masked line-by-line reveal + parallax Cypher
   ========================================================== */
const HeadingLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-2">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
    <span className="sr-only"> </span>
  </span>
);

const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const cypherY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const cypherScale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const headingY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid={SERVICES.hero}
      className="relative overflow-hidden min-h-[92vh] flex items-center"
    >
      {/* Background layers */}
      <motion.div
        style={{ y: gridY }}
        className="absolute inset-0 -z-10 bg-grid opacity-60 animate-grid-pan"
      />
      <div className="absolute inset-0 -z-10 bg-radial-glow" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-slate-950/70 to-[#030712]" />

      {/* Soft floating orbs */}
      <motion.div
        aria-hidden
        className="absolute top-[15%] left-[8%] h-56 w-56 rounded-full bg-cyan-500/10 blur-3xl -z-10"
        animate={{ y: [0, -18, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-[10%] right-[12%] h-72 w-72 rounded-full bg-blue-500/10 blur-3xl -z-10"
        animate={{ y: [0, 18, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <Container className="py-20 sm:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <motion.div style={{ y: headingY }} className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 mb-8"
            >
              <CircleDot className="h-3.5 w-3.5 text-cyan-400 animate-pulse" />
              <span className="font-mono text-[11px] font-bold uppercase tracking-[0.28em] text-cyan-400">
                JK Academy · Services
              </span>
            </motion.div>

            <h1
              data-testid={SERVICES.heroHeadline}
              className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl xl:text-[5.5rem] leading-[1.02]"
            >
              <HeadingLine delay={0.15}>AI Solutions</HeadingLine>
              <HeadingLine delay={0.28}>
                That Help <span className="text-cyan-400 text-glow-cyan">Small</span>
              </HeadingLine>
              <HeadingLine delay={0.4}>
                <span className="relative inline-block">
                  <span className="relative z-10">Businesses</span>
                  <span className="absolute -bottom-1 left-0 right-0 h-2 bg-cyan-500/30 -z-0" />
                </span>{" "}
                Grow.
              </HeadingLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.75 }}
              className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed font-mono"
            >
              We help small businesses attract more customers, save time through{" "}
              <span className="text-cyan-300">AI automation</span>, and build a stronger online presence with modern websites, AI tools, and employee training.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.75 }}
              className="mt-10 flex flex-col sm:flex-row items-start gap-4"
            >
              <button
                onClick={onBook}
                data-testid={SERVICES.heroCtaPrimary}
                className="group inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_48px_rgba(6,182,212,0.75)] transition-all"
              >
                <Sparkles className="h-4 w-4" />
                Book a Free Consultation
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="#services-grid"
                data-testid={SERVICES.heroCtaSecondary}
                className="group inline-flex items-center justify-center gap-3 bg-transparent hover:bg-cyan-500/10 text-cyan-300 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 border border-cyan-400/60 hover:border-cyan-300 transition-all"
              >
                Explore Services
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>
          </motion.div>

          {/* Cypher — parallax spotlight moment */}
          <motion.div
            style={{ y: cypherY, scale: cypherScale }}
            className="lg:col-span-5 relative hidden md:block"
            aria-hidden
          >
            <div className="relative aspect-square max-w-[520px] mx-auto lg:ml-auto">
              {/* Spotlight cone */}
              <div
                className="absolute -top-6 left-1/2 -translate-x-1/2 w-[80%] h-[110%] pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 0%, rgba(6,182,212,0.35), rgba(6,182,212,0.05) 45%, transparent 70%)",
                }}
              />
              {/* Clipped picture frame */}
              <div
                className="absolute inset-[6%] border border-cyan-500/30"
                style={{
                  clipPath:
                    "polygon(0 8%, 8% 0, 92% 0, 100% 8%, 100% 92%, 92% 100%, 8% 100%, 0 92%)",
                  background:
                    "linear-gradient(180deg, rgba(6,182,212,0.06), rgba(3,7,18,0.4))",
                }}
              />
              {/* Corner glyphs */}
              <div className="absolute top-[6%] left-[6%] h-5 w-5 border-t-2 border-l-2 border-cyan-400" />
              <div className="absolute top-[6%] right-[6%] h-5 w-5 border-t-2 border-r-2 border-cyan-400" />
              <div className="absolute bottom-[6%] left-[6%] h-5 w-5 border-b-2 border-l-2 border-cyan-400" />
              <div className="absolute bottom-[6%] right-[6%] h-5 w-5 border-b-2 border-r-2 border-cyan-400" />

              <motion.img
                src={CYPHER_IMG}
                alt=""
                className="relative z-10 w-full h-full object-contain drop-shadow-[0_0_60px_rgba(6,182,212,0.35)]"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Floor reflection */}
              <div className="absolute bottom-[3%] left-[20%] right-[20%] h-[3px] bg-gradient-to-r from-transparent via-cyan-400/80 to-transparent blur-sm" />
            </div>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.6 }}
          className="mt-16 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500"
        >
          <span>Scroll</span>
          <motion.span
            animate={{ x: [0, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          >
            →
          </motion.span>
          <span>07 chapters</span>
        </motion.div>
      </Container>
    </section>
  );
};

/* ==========================================================
   3. MARQUEE — editorial slow-scrolling manifesto
   ========================================================== */
const MARQUEE_WORDS = [
  "AI Websites",
  "★",
  "Automation",
  "★",
  "Training",
  "★",
  "Consulting",
  "★",
  "Small Business",
  "★",
  "Cypher × You",
  "★",
];

const Marquee = () => (
  <section
    data-testid={SERVICES.marquee}
    className="relative py-8 border-y border-white/10 bg-slate-950 overflow-hidden"
  >
    <motion.div
      className="flex gap-14 whitespace-nowrap will-change-transform"
      animate={{ x: ["0%", "-50%"] }}
      transition={{ duration: 40, ease: "linear", repeat: Infinity }}
    >
      {[...MARQUEE_WORDS, ...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
        <span
          key={i}
          className={`font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter ${
            w === "★" ? "text-cyan-400" : "text-white/30"
          }`}
        >
          {w}
        </span>
      ))}
    </motion.div>
  </section>
);

/* ==========================================================
   4. OUR SERVICES
   ========================================================== */
const SERVICES_LIST = [
  {
    slug: "websites",
    icon: Globe,
    title: "AI Website Design",
    tag: "Bestseller",
    description:
      "Professional websites designed to generate leads, build trust, and grow your business.",
    features: [
      "Mobile responsive",
      "SEO optimized",
      "Fast loading",
      "Contact forms",
      "AI chatbot ready",
    ],
    cta: "Learn More",
  },
  {
    slug: "automation",
    icon: Cog,
    title: "AI Automation",
    tag: null,
    description: "Automate repetitive business tasks using AI.",
    features: [
      "Lead capture",
      "Customer follow-up",
      "Appointment reminders",
      "Email automation",
      "Workflow automation",
    ],
    cta: "Learn More",
  },
  {
    slug: "training",
    icon: GraduationCap,
    title: "AI Business Training",
    tag: null,
    description:
      "Train your team to confidently use ChatGPT, Claude, Gemini, and Microsoft Copilot.",
    features: ["On-site workshops", "Virtual training", "Custom company programs"],
    cta: "Learn More",
  },
  {
    slug: "consulting",
    icon: Compass,
    title: "AI Consulting",
    tag: null,
    description:
      "We identify opportunities where AI can save your company time, money, and manual work.",
    features: [
      "Business audit",
      "AI opportunity roadmap",
      "ROI-driven prioritization",
    ],
    cta: "Schedule Consultation",
  },
];

const ServiceCard = ({ s, onCta }) => {
  const Icon = s.icon;
  return (
    <motion.div
      data-testid={SERVICES.serviceCard(s.slug)}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="group relative border border-white/10 bg-slate-900/50 backdrop-blur-md p-8 hover:border-cyan-500/60 hover:bg-slate-900/70 transition-colors flex flex-col overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/0 to-transparent group-hover:via-cyan-500/70 transition-all duration-500" />
      <div className="absolute -inset-16 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-cyan-500/[0.08] via-transparent to-transparent transition-opacity duration-500 pointer-events-none" />

      <div className="flex items-start justify-between mb-6">
        <div className="h-12 w-12 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 transition-colors">
          <Icon className="h-5 w-5" />
        </div>
        {s.tag && (
          <span className="px-2 py-0.5 bg-cyan-500 text-slate-950 font-mono text-[9px] uppercase tracking-[0.22em] font-bold">
            {s.tag}
          </span>
        )}
      </div>

      <h3 className="font-display text-2xl font-bold text-white leading-tight">
        {s.title}
      </h3>
      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.description}</p>

      <ul className="mt-5 space-y-2 flex-1">
        {s.features.map((f) => (
          <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
            <CheckCircle2 className="h-4 w-4 text-cyan-400 shrink-0" />
            <span className="font-mono">{f}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={onCta}
        data-testid={SERVICES.serviceCta(s.slug)}
        className="mt-7 inline-flex items-center justify-between gap-2 text-cyan-300 font-mono uppercase tracking-[0.18em] text-xs font-bold border-t border-white/10 pt-4 group-hover:text-cyan-200 transition-colors"
      >
        {s.cta}
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
      </button>
    </motion.div>
  );
};

const OurServices = ({ onBook }) => (
  <section id="services-grid" className="relative py-24 sm:py-32">
    <Container>
      <Reveal>
        <SectionLabel n="01">Our Services</SectionLabel>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-white max-w-3xl">
          Four ways we help your business{" "}
          <span className="text-cyan-400">move faster</span>.
        </h2>
      </Reveal>

      <div
        data-testid={SERVICES.servicesGrid}
        className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6"
      >
        {SERVICES_LIST.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.08}>
            <ServiceCard s={s} onCta={onBook} />
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

/* ==========================================================
   5. INDUSTRIES
   ========================================================== */
const INDUSTRIES = [
  { slug: "restaurants", biz: "Restaurant", icon: Utensils, title: "Restaurants", desc: "Fill more tables and simplify reservations with AI." },
  { slug: "hvac", biz: "HVAC", icon: Wind, title: "HVAC", desc: "Route more service calls, faster, without adding staff." },
  { slug: "plumbing", biz: "Plumbing", icon: Wrench, title: "Plumbing", desc: "24/7 lead capture and automated dispatch." },
  { slug: "lawncare", biz: "Lawn Care", icon: Trees, title: "Lawn Care", desc: "Seasonal campaigns and smart scheduling on autopilot." },
  { slug: "fencing", biz: "Fencing", icon: Hammer, title: "Fencing", desc: "Instant quotes and follow-ups that don't slip through." },
  { slug: "contractors", biz: "Contractor", icon: Building2, title: "Contractors", desc: "Turn every inbound lead into a booked estimate." },
  { slug: "professional", biz: "Professional Services", icon: Briefcase, title: "Professional Services", desc: "Modern site + AI intake for consultants and firms." },
  { slug: "small-biz", biz: "Small Business", icon: Store, title: "Small Businesses", desc: "One partner for site, automation, and staff training." },
];

const IndustryCard = ({ item, onSelect }) => {
  const Icon = item.icon;
  return (
    <motion.button
      type="button"
      data-testid={SERVICES.industryCard(item.slug)}
      onClick={() => onSelect?.(item.biz)}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="group relative w-full text-left border border-white/10 bg-slate-900/40 backdrop-blur-sm p-6 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-colors"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/0 to-transparent group-hover:via-cyan-500/70 transition-all duration-500" />
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 shrink-0 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 transition-colors">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display text-base sm:text-lg font-bold text-white leading-none">
            {item.title}
          </h3>
          <p className="mt-1 font-mono text-xs text-slate-400 leading-snug">
            {item.desc}
          </p>
        </div>
      </div>
    </motion.button>
  );
};

const Industries = ({ onSelect }) => (
  <section className="relative py-24 sm:py-32 border-t border-white/5">
    <Container>
      <Reveal>
        <SectionLabel n="02">Industries We Serve</SectionLabel>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-white max-w-3xl">
          Built for real, <span className="text-cyan-400">local</span> businesses.
        </h2>
      </Reveal>

      <div
        data-testid={SERVICES.industriesGrid}
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
      >
        {INDUSTRIES.map((it, i) => (
          <Reveal key={it.slug} delay={i * 0.05}>
            <IndustryCard item={it} onSelect={onSelect} />
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

/* ==========================================================
   6. WHY CHOOSE JK ACADEMY
   ========================================================== */
const WHY_ITEMS = [
  { slug: "expertise", icon: Sparkles, title: "AI Expertise", desc: "Deep hands-on knowledge across every major AI platform." },
  { slug: "biz", icon: Briefcase, title: "Business Experience", desc: "Real workflows, not academic theory." },
  { slug: "afford", icon: DollarSign, title: "Affordable Solutions", desc: "Priced for small businesses — not enterprise budgets." },
  { slug: "personal", icon: Handshake, title: "Personalized Service", desc: "You talk to a founder, not a support queue." },
  { slug: "training", icon: BookOpen, title: "Training Included", desc: "Every project ships with your team knowing how to run it." },
  { slug: "future", icon: ShieldCheck, title: "Future Ready", desc: "Built to evolve as new AI tools land — no rebuilds." },
];

const WhyCard = ({ item }) => {
  const Icon = item.icon;
  return (
    <motion.div
      data-testid={SERVICES.whyCard(item.slug)}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35 }}
      className="group relative border border-white/10 bg-slate-900/40 backdrop-blur-sm p-7 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-colors"
    >
      <div className="h-10 w-10 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 mb-5 group-hover:bg-cyan-500/20 transition-colors">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-display text-lg sm:text-xl font-bold text-white">{item.title}</h3>
      <p className="mt-2 text-sm text-slate-400 leading-relaxed">{item.desc}</p>
    </motion.div>
  );
};

const WhyUs = () => (
  <section className="relative py-24 sm:py-32 border-t border-white/5">
    <div className="absolute inset-0 -z-10 bg-radial-glow opacity-30" />
    <Container>
      <Reveal>
        <SectionLabel n="03">Why JK Academy</SectionLabel>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-white max-w-3xl">
          The advantage of a partner who&apos;s{" "}
          <span className="text-cyan-400">done this</span> before.
        </h2>
      </Reveal>
      <div
        data-testid={SERVICES.whyGrid}
        className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
      >
        {WHY_ITEMS.map((it, i) => (
          <Reveal key={it.slug} delay={i * 0.06}>
            <WhyCard item={it} />
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

/* ==========================================================
   7. OUR PROCESS
   ========================================================== */
const STEPS = [
  { n: "01", title: "Discover", desc: "We learn about your business." },
  { n: "02", title: "Plan", desc: "Create a customized AI strategy." },
  { n: "03", title: "Build", desc: "Develop your website or automation." },
  { n: "04", title: "Launch", desc: "Deploy your new solution." },
  { n: "05", title: "Support", desc: "Ongoing improvements and AI guidance." },
];

const Process = () => (
  <section id="process" className="relative py-24 sm:py-32 border-t border-white/5">
    <Container>
      <Reveal>
        <SectionLabel n="04">Our Process</SectionLabel>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-white max-w-3xl">
          Five steps. <span className="text-cyan-400">One outcome.</span>
        </h2>
      </Reveal>

      <div className="mt-16 relative" data-testid={SERVICES.processSteps}>
        {/* Connecting line desktop */}
        <div className="hidden lg:block absolute top-16 left-[6%] right-[6%] h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div
                data-testid={SERVICES.processStep(i + 1)}
                className="relative"
              >
                {/* Node */}
                <div className="relative mx-auto lg:mx-0 h-14 w-14 flex items-center justify-center border border-cyan-500/50 bg-slate-950 text-cyan-300 font-mono font-bold text-lg z-10">
                  {s.n}
                  <div className="absolute inset-0 -z-10 bg-cyan-500/10 blur-md" />
                </div>
                <div className="mt-5 text-center lg:text-left">
                  <h3 className="font-display text-xl font-bold text-white">{s.title}</h3>
                  <p className="mt-1.5 font-mono text-xs text-slate-400 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ==========================================================
   8. FAQ
   ========================================================== */
const FAQ = [
  {
    q: "How much does a website cost?",
    a: "Most small-business projects land between $1,500 and $5,000 depending on scope. We give you a fixed quote after a free 20-minute discovery call — no surprises.",
  },
  {
    q: "How long does a project take?",
    a: "Websites typically ship in 2–4 weeks. Automation builds and training programs range from 1 to 6 weeks based on complexity.",
  },
  {
    q: "Can you train my employees?",
    a: "Yes. We run virtual and on-site workshops for ChatGPT, Claude, Gemini, and Microsoft Copilot — always tailored to your team's real workflows.",
  },
  {
    q: "Can AI automate my business?",
    a: "In most cases, yes — anything repetitive (lead capture, follow-ups, reminders, reporting) is a great fit. We start with a free audit to identify the highest-ROI wins.",
  },
  {
    q: "Do you provide ongoing support?",
    a: "Absolutely. We offer monthly retainers for continuous improvements, training refreshers, and adding new AI capabilities as tools evolve.",
  },
];

const FaqItem = ({ item, i, open, onToggle }) => {
  const panelId = `faq-panel-${i}`;
  return (
    <div
      data-testid={SERVICES.faqItem(i)}
      className="border-b border-white/10 last:border-0"
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={`${SERVICES.faqItem(i)}-toggle`}
        className="w-full flex items-center justify-between text-left py-6 gap-6 group"
      >
        <span className="font-display text-lg sm:text-xl font-semibold text-white group-hover:text-cyan-300 transition-colors">
          {item.q}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="shrink-0 h-9 w-9 flex items-center justify-center border border-cyan-500/40 bg-slate-950 text-cyan-300"
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </button>
      <motion.div
        id={panelId}
        role="region"
        aria-hidden={!open}
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-6 pr-16 text-slate-300 font-mono text-sm leading-relaxed">
          {item.a}
        </p>
      </motion.div>
    </div>
  );
};

const Faq = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="relative py-24 sm:py-32 border-t border-white/5">
      <Container>
        <Reveal>
          <SectionLabel n="05">Frequently Asked</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-white max-w-3xl">
            The stuff most people ask{" "}
            <span className="text-cyan-400">on the first call</span>.
          </h2>
        </Reveal>

        <div
          data-testid={SERVICES.faqList}
          className="mt-14 max-w-3xl border-t border-white/10"
        >
          {FAQ.map((item, i) => (
            <FaqItem
              key={item.q}
              item={item}
              i={i}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </Container>
    </section>
  );
};

/* ==========================================================
   9. BIG CTA BANNER
   ========================================================== */
const BigCta = ({ onBook }) => (
  <section
    data-testid={SERVICES.bigCta}
    className="relative py-24 sm:py-32 overflow-hidden border-t border-white/5"
  >
    <div className="absolute inset-0 -z-10 bg-grid opacity-30" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-cyan-500/[0.05] to-transparent" />

    {/* Floating cypher accent */}
    <motion.img
      src={CYPHER_IMG}
      alt=""
      aria-hidden
      className="absolute -bottom-10 right-[-40px] w-64 sm:w-80 opacity-90 hidden md:block drop-shadow-[0_0_60px_rgba(6,182,212,0.35)]"
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    />

    <Container>
      <div className="relative max-w-3xl border border-cyan-500/40 bg-slate-900/60 backdrop-blur-md p-10 sm:p-16 shadow-[0_0_80px_rgba(6,182,212,0.2)]">
        <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
        <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
        <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
        <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />

        <SectionLabel n="06">Ready When You Are</SectionLabel>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
          Let&apos;s build the{" "}
          <span className="text-cyan-400 text-glow-cyan">future</span> of your business.
        </h2>
        <p className="mt-6 max-w-xl text-base text-slate-300 font-mono leading-relaxed">
          Schedule a free consultation and discover how AI can help your business grow.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
          <button
            data-testid={SERVICES.bigCtaBook}
            onClick={onBook}
            className="group inline-flex items-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_48px_rgba(6,182,212,0.75)] transition-all"
          >
            <Sparkles className="h-4 w-4" />
            Book Consultation
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid={SERVICES.bigCtaContact}
            onClick={onBook}
            className="group inline-flex items-center gap-3 bg-transparent hover:bg-cyan-500/10 text-cyan-300 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 border border-cyan-400/60 hover:border-cyan-300 transition-all"
          >
            <Send className="h-4 w-4" />
            Contact Us
          </button>
        </div>
      </div>
    </Container>
  </section>
);

/* ==========================================================
   10. FOOTER (services-scoped)
   ========================================================== */
const Footer = () => (
  <footer className="border-t border-white/10 bg-slate-950/60 py-14">
    <Container className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src={LOGO_URL} alt="JK Academy" className="h-8 w-8 object-contain" />
        <div className="leading-tight">
          <div className="font-display font-extrabold text-white text-sm">JK ACADEMY</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            AI for small business
          </div>
        </div>
      </div>
      <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
        <Link to="/" className="hover:text-cyan-300 transition-colors">Home</Link>
        <a href="#services-grid" className="hover:text-cyan-300 transition-colors">Services</a>
        <a href="#process" className="hover:text-cyan-300 transition-colors">Process</a>
        <a href="#faq" className="hover:text-cyan-300 transition-colors">FAQ</a>
      </nav>
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        © {new Date().getFullYear()} JK Academy
      </p>
    </Container>
  </footer>
);

/* ==========================================================
   PAGE
   ========================================================== */
export default function ServicesPage() {
  useLenis();
  const [modalOpen, setModalOpen] = useState(false);
  const [prefillBusiness, setPrefillBusiness] = useState("");

  // SEO — set title + meta description + JSON-LD schema
  useEffect(() => {
    document.title = "AI Business Services | JK Academy";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "JK Academy helps small businesses grow with AI websites, automation, consulting, and employee AI training."
    );

    const schema = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "JK Academy",
      url: "https://jkacademy.tech/services",
      description:
        "AI websites, automation, consulting, and employee AI training for small businesses.",
      sameAs: ["https://jkacademy.tech", "https://jkacademy.academy"],
      areaServed: "US",
      serviceType: [
        "AI Website Design",
        "AI Automation",
        "AI Business Training",
        "AI Consulting",
      ],
    };
    let script = document.getElementById("jk-services-schema");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "jk-services-schema";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      document.title = "JK Academy — 10 ChatGPT Prompts That Save Time";
    };
  }, []);

  const openBook = () => {
    setPrefillBusiness("");
    setModalOpen(true);
  };
  const openBookWith = (biz) => {
    setPrefillBusiness(biz || "");
    setModalOpen(true);
  };

  return (
    <div
      data-testid={SERVICES.page}
      className="min-h-screen bg-[#030712] text-slate-100"
    >
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          className: "font-mono",
          style: {
            background: "#0F172A",
            border: "1px solid rgba(6,182,212,0.4)",
            color: "#F8FAFC",
            borderRadius: 0,
          },
        }}
      />
      <Header onBook={openBook} />
      <main>
        <Hero onBook={openBook} />
        <Marquee />
        <OurServices onBook={openBook} />
        <Industries onSelect={openBookWith} />
        <WhyUs />
        <Process />
        <Faq />
        <BigCta onBook={openBook} />
      </main>
      <Footer />
      <ConsultationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultBusiness={prefillBusiness}
      />
    </div>
  );
}
